import type { DemoStation } from '@/types/demo';

// ── Station status and formatting ───────────────────────────────────────────
//
// These are pure, and they live here rather than in src/lib/api/demoStations.ts
// so that testing them does not drag in the Supabase client. That client throws
// at import time when VITE_SUPABASE_* are absent, which is true of CI - a test
// importing them through the api module passes locally off the developer's .env
// and fails on a runner.
//
// demoStations.ts re-exports all three, so existing callers are unaffected.

/**
 * Station is considered online if status is online and last_seen within 5 minutes.
 *
 * 5 min = 10x the 30 s heartbeat interval - generous window to account for
 * query caching, network jitter, and slow heartbeat rounds.
 *
 * The stored `status` column cannot be trusted on its own: nothing stamps a
 * station offline when it stops checking in, so rows sit at 'online'
 * indefinitely - the fleet has held rows reading 'online' that last checked in
 * weeks earlier. The freshness of last_seen is what settles it.
 */
export function isStationOnline(station: Pick<DemoStation, 'last_seen' | 'status'>): boolean {
  if (station.status === 'offline' || station.status === 'retiring') return false;
  if (!station.last_seen) return station.status === 'online'; // no heartbeat yet, trust status field
  return Date.now() - new Date(station.last_seen).getTime() < 5 * 60 * 1000;
}

export function formatUptime(seconds: number | null): string {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * Whether the kiosk shell stays suspended once the two modes settle.
 *
 * Demo Mode and Admin Mode are independent - either can be on without the
 * other - but both need the shell down: Demo Mode so a customer sees a desktop
 * instead of a locked kiosk, Admin Mode so a technician has Explorer to run an
 * installer from. kiosk_suspended is that shared consequence, and nothing else;
 * it stopped being a synonym for Demo Mode when the two came apart.
 */
export function kioskSuspendedFor(demoMode: boolean, adminMode: boolean): boolean {
  return demoMode || adminMode;
}

/**
 * Days a station may sit in Admin Mode before the dashboard flags it as
 * forgotten. Longer than any real vendor install session, short enough that a
 * machine left open turns up before it becomes normal to see.
 */
export const ADMIN_MODE_STALE_DAYS = 3;

/**
 * True when a station has been in Admin Mode long enough to look forgotten
 * rather than in use.
 *
 * Two ways a station gets here. A persistent station has no relock timer at all
 * by design, so nothing but a person ever ends its unlock. A timed one re-arms
 * its deadline at boot, which means a station powered off mid-unlock and left
 * off stays flagged open for as long as it stays down. Both are worth a warning
 * and neither is an error, so this only reports; it never relocks anything.
 *
 * Returns false when there is no start time: an unlock nothing recorded is not
 * evidence of an old one.
 */
export function isAdminModeStale(
  adminMode: boolean | null | undefined,
  since: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (adminMode !== true || !since) return false;
  const started = new Date(since).getTime();
  if (!Number.isFinite(started)) return false;
  return now - started >= ADMIN_MODE_STALE_DAYS * 86_400_000;
}

/** How long the current Admin Mode unlock has been open, e.g. "5d 2h". */
export function formatAdminModeAge(since: string | null | undefined, now: number = Date.now()): string {
  if (!since) return '—';
  const started = new Date(since).getTime();
  if (!Number.isFinite(started)) return '—';
  return formatUptime(Math.floor((now - started) / 1000));
}

/**
 * True while a USB unlock window is still open. Reads an expiry rather than a
 * boolean so a station that went offline mid-unlock decays to locked instead of
 * showing as open forever.
 */
export function isUsbUnlocked(until: string | null | undefined): boolean {
  if (!until) return false;
  const ms = new Date(until).getTime();
  return Number.isFinite(ms) && ms > Date.now();
}

/** How much of a USB unlock window is left, e.g. "45m" or "2h 10m". */
export function formatRemaining(until: string | null | undefined): string {
  if (!until) return '—';
  const secs = Math.floor((new Date(until).getTime() - Date.now()) / 1000);
  if (secs <= 0) return '—';
  if (secs < 60) return '<1m';
  return formatUptime(secs);
}

export function formatLastSeen(lastSeen: string | null): string {
  if (!lastSeen) return 'Never';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(lastSeen).getTime()) / 1000));
  if (diff < 5) return 'Just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
